/**
 * SafetyErectionConfirm · iter303ak-fix29 — Work In Progress · T20 · Action 10
 * ─────────────────────────────────────────────────────────────────────────
 * Single-record inspection log:
 *   · Inspector name (auto-filled from current user, editable)
 *   · Inspection date + auto-datestamp
 *   · Notes (defects / observations)
 * Persists on Continue to parent appraisal `safety_erection`.
 * Photo upload deferred until file storage integration.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, ShieldCheck, AlertTriangle, ClipboardCheck } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function SafetyErectionConfirm({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;

  const priorRecord = useMemo(() => (
    capturedData?.appraisal_record?.safety_erection
    || capturedData?.appraisal?.safety_erection
    || {}
  ), [capturedData]);

  const [inspector, setInspector] = useState(value?.inspector_name || priorRecord.inspector_name || '');
  const [inspectedDate, setInspectedDate] = useState(() => {
    const iso = value?.inspected_at || priorRecord.inspected_at;
    return iso ? iso.slice(0, 10) : new Date().toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState(value?.notes || priorRecord.notes || '');
  const [confirmed, setConfirmed] = useState(!!(value?.confirmed || priorRecord.inspected_at));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  // Auto-fill inspector from current user if empty.
  const loadCurrentUser = useCallback(async () => {
    if (inspector) return;
    try {
      const { data } = await api.get('/auth/me');
      const name = data?.name || data?.username || data?.email || '';
      if (name) setInspector(name);
    } catch { /* silent */ }
  }, [inspector]);

  useEffect(() => { loadCurrentUser(); }, [loadCurrentUser]);

  useEffect(() => {
    if (onChange) {
      const inspected_at = confirmed && inspectedDate
        ? new Date(`${inspectedDate}T09:00:00`).toISOString()
        : null;
      onChange({
        inspector_name: inspector,
        inspected_at,
        notes,
        confirmed,
      });
    }
  }, [inspector, inspectedDate, notes, confirmed]);   

  const persistAndContinue = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      const payload = {
        inspector_name: inspector,
        inspected_at: new Date(`${inspectedDate}T09:00:00`).toISOString(),
        notes,
      };
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, { safety_erection: payload });
      }
      onNext();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canContinue = !!inspector.trim() && !!inspectedDate && confirmed;

  return (
    <div className="space-y-4" data-testid="wf-safety-erection-confirm">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-emerald-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-900">Physical Safety Inspection</div>
            <div className="text-sm mt-1 text-emerald-800">
              {params.prompt || 'Physically inspect the scaffold / safety setup and record the inspection.'}
            </div>
            <div className="text-[11px] mt-2 text-emerald-700">
              Photo upload will be added in a later iteration once file storage is wired.
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 border border-slate-200 rounded-lg bg-white space-y-3">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Inspector <span className="text-rose-500">*</span></label>
          <input type="text" value={inspector} onChange={(e) => setInspector(e.target.value)}
            placeholder="Name of person who inspected on site"
            data-testid="wf-se-inspector"
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Inspection date <span className="text-rose-500">*</span></label>
          <input type="date" value={inspectedDate} onChange={(e) => setInspectedDate(e.target.value)}
            data-testid="wf-se-date"
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Notes (defects / observations)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. scaffold to spec · guardrails secure · minor debris to clear · etc."
            data-testid="wf-se-notes"
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" rows={4} />
        </div>
        <label className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="wf-se-confirmed"
            className="w-5 h-5" />
          <ClipboardCheck size={16} className={confirmed ? 'text-emerald-600' : 'text-slate-400'} />
          <span className="text-sm font-medium text-slate-700">Safety erected — inspected & signed off on site</span>
        </label>
      </div>

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!canContinue || saving}
            data-testid="wf-se-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
